import { isProduction } from 'utils/env';

var chunkRecovery = require('./chunkRecoveryBootstrap');

var DEFAULT_ENDPOINT = '/mw/error/crm-react-log';

function nowIso() {
	try { return new Date().toISOString(); } catch (_) { return '';}
}

function nowMs() {
	try { return Date.now(); } catch (_) { return 0; }
}

function safeString(x) {
	try { return typeof x === 'string' ? x : JSON.stringify(x); } catch (_) { return String(x); }
}

function buildEventFingerprint(event) {
	return [
		event && event.type,
		event && event.name,
		event && event.message,
		event && event.stack,
		safeString(event && event.custom),
	].join('|');
}

function safeGetBuild() {
	try {
		// eslint-disable-next-line no-undef
		return (typeof BUILD !== 'undefined') ? String(BUILD) : '';
	} catch (e) {
		return '';
	}
}

function safeGetMode() {
	try {
		// eslint-disable-next-line no-undef
		return (typeof MODE !== 'undefined') ? String(MODE) : '';
	} catch (e) {
		return '';
	}
}

function randomId() {
	return `${nowMs().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function getOrCreateLocalId(key) {
	if (typeof window === 'undefined' || !window.localStorage) {
		return randomId();
	}

	try {
		var existing = window.localStorage.getItem(key);
		if (existing) {
			return existing;
		}
		var next = randomId();
		window.localStorage.setItem(key, next);
		return next;
	} catch (e) {
		return randomId();
	}
}

function parseTopFrame(stack) {
	if (!stack) return null;
	var m = stack.match(/https?:\/\/[^\s)]+:\d+:\d+/);
	if (!m) return null;
	var url = m[0];
	var mm = url.match(/(https?:\/\/.+):(\d+):(\d+)/);
	if (!mm) return null;
	return { file: mm[1], line: parseInt(mm[2], 10), column: parseInt(mm[3], 10) };
}

function shouldReloadOnChunkError(error) {
	return chunkRecovery.shouldRecoverFromChunkError(error);
}

function extractReloadableStaticAssetUrl(event) {
	return chunkRecovery.extractStaticAssetUrl(event);
}

function createChunkLoadErrorHandler(locationObject, replace, reload, now, storageObject) {
	var runtimeRecovery = typeof window !== 'undefined' && window.__CRM_CHUNK_RECOVERY__;
	var controller = runtimeRecovery || chunkRecovery;
	return controller.createHandler({
		locationObject: locationObject,
		replace: replace,
		reload: reload,
		now: now,
		storageObject: storageObject
	});
}

function subscribeErrorReporterWindowError(handleWindowError, windowObject) {
	if (!windowObject && typeof window !== 'undefined') {
		windowObject = window;
	}
	if (!windowObject || typeof windowObject.addEventListener !== 'function') {
		return;
	}
	windowObject.addEventListener('error', handleWindowError, true);
}

function subscribeErrorReporterUnhandledRejection(handleUnhandledRejection, windowObject) {
	if (!windowObject && typeof window !== 'undefined') {
		windowObject = window;
	}
	if (!windowObject || typeof windowObject.addEventListener !== 'function') {
		return;
	}
	windowObject.addEventListener('unhandledrejection', handleUnhandledRejection, true);
}

class ErrorReporter {
	constructor() {
		var cfg = (typeof window !== 'undefined' && window.__RUM_CONFIG__) || {};
		var build = cfg.build || safeGetBuild();
		var mode = cfg.mode || safeGetMode();

		this.endpoint = cfg.endpoint || DEFAULT_ENDPOINT;
		this.serviceName = cfg.serviceName || 'crm-frontend';
		// Prefer explicit cfg.environment, then BUILD (except tests), then fallback.
		this.environment = cfg.environment || ((build && build !== 'test') ? build : (isProduction ? 'production' : 'development'));
		this.flushInterval = cfg.flushInterval || 3000; // ms
		this.batchSize = cfg.batchSize || 20;
		this.maxQueue = cfg.maxQueue || 200;
		this.requestTimeout = (typeof cfg.requestTimeout === 'number') ? cfg.requestTimeout : 10000;
		this.maxRetries = (typeof cfg.maxRetries === 'number') ? cfg.maxRetries : 1;
		this.dedupeWindowMs = (typeof cfg.dedupeWindowMs === 'number') ? cfg.dedupeWindowMs : 10000;
		this.debug = !!cfg.debug;

		this.build = build || null;
		this.mode = mode || null;
		this.release = cfg.release || (this.build || this.environment);
		this.clientSessionId = cfg.clientSessionId || getOrCreateLocalId('rum.clientSessionId');
		this.pageViewId = cfg.pageViewId || randomId();

		this.user = null;
		this.labels = {};
		if (this.build) this.labels.build = this.build;
		if (this.mode) this.labels.mode = this.mode;
		if (this.release) this.labels.release = this.release;
		if (this.clientSessionId) this.labels.clientSessionId = this.clientSessionId;
		if (this.pageViewId) this.labels.pageViewId = this.pageViewId;
		this.queue = [];
		this.timer = null;
		this.inFlight = false;
		this.retryCount = 0;
		this.recentEventTimestamps = {};

		// Inline bootstrap covers initial bundle failures; tests/non-HTML entrypoints install equivalent listeners here.
		var runtimeRecovery = typeof window !== 'undefined' && window.__CRM_CHUNK_RECOVERY__;
		if (runtimeRecovery) {
			runtimeRecovery.install();
		} else {
			var chunkLoadErrorHandler = createChunkLoadErrorHandler();
			subscribeErrorReporterWindowError(chunkLoadErrorHandler);
			subscribeErrorReporterUnhandledRejection(chunkLoadErrorHandler);
		}
	}

	setUser(user) { this.user = user; }
	addLabels(labels) {
		var next = {};
		for (var k in this.labels) next[k] = this.labels[k];
		for (var k2 in labels) next[k2] = labels[k2];
		this.labels = next;
	}

	captureMessage(message, custom) {
		if (!custom) custom = {};
		this._enqueue(this._makeEvent('message', { message: safeString(message), custom: custom }));
	}

	captureError(error, custom) {
		if (!custom) {
			custom = {};
		}

		var message = '';
		var stack = '';
		var name = 'Error';

		if (error && error instanceof Error) {
			message = error.message || String(error);
			stack = error.stack || '';
			name = error.name || 'Error';
		} else if (typeof error === 'string') {
			message = error;
		} else {
			message = safeString(error);
		}

		var top = parseTopFrame(stack);
		var event = this._makeEvent('error', {
			name: name,
			message: message,
			stack: stack,
			custom: custom,
			mapped: null,
			minTopFrame: top,
		});
		this._enqueue(event);
	}

	_makeEvent(type, payload) {
		var base = {
			type: type,
			service: this.serviceName,
			environment: this.environment,
			release: this.release,
			clientSessionId: this.clientSessionId,
			pageViewId: this.pageViewId,
			ts: nowIso(),
			url: (typeof window !== 'undefined' && window.location) ? window.location.href : null,
			referrer: (typeof document !== 'undefined' && document.referrer) ? document.referrer : null,
			userAgent: (typeof navigator !== 'undefined' && navigator.userAgent) ? navigator.userAgent : null,
			user: this.user || null,
			labels: this.labels
		};
		// shallow merge
		for (var k in payload) base[k] = payload[k];
		return base;
	}

	_enqueue(event) {
		try {
			if (this._isDuplicate(event)) {
				return;
			}

			this.queue.push(event);
			if (this.queue.length > this.maxQueue) {
				this.queue.splice(0, this.queue.length - this.maxQueue);
			}
			if (this.inFlight) {
				return;
			}
			if (this.queue.length >= this.batchSize) {
				this._flush();
			} else {
				this._scheduleFlush(this.flushInterval);
			}
		} catch (e) {
			if (this.debug && typeof console !== 'undefined') console.warn('[rum] enqueue failed', e);
		}
	}

	_isDuplicate(event) {
		if (this.dedupeWindowMs <= 0) {
			return false;
		}

		var timestamp = nowMs();
		var fingerprint = buildEventFingerprint(event);
		var previousTimestamp = this.recentEventTimestamps[fingerprint];
		this.recentEventTimestamps[fingerprint] = timestamp;

		var keys = Object.keys(this.recentEventTimestamps);
		if (keys.length > this.maxQueue * 2) {
			for (var i = 0; i < keys.length; i++) {
				if (timestamp - this.recentEventTimestamps[keys[i]] >= this.dedupeWindowMs) {
					delete this.recentEventTimestamps[keys[i]];
				}
			}

			keys = Object.keys(this.recentEventTimestamps);
			keys.sort((a, b) => this.recentEventTimestamps[a] - this.recentEventTimestamps[b]);
			while (keys.length > this.maxQueue * 2) {
				delete this.recentEventTimestamps[keys.shift()];
			}
		}

		return typeof previousTimestamp === 'number'
			&& timestamp - previousTimestamp < this.dedupeWindowMs;
	}

	_scheduleFlush(delay) {
		if (!this.queue.length || this.inFlight || this.timer) {
			return;
		}

		var self = this;
		this.timer = setTimeout(function () {
			self.timer = null;
			self._flush();
		}, delay);
	}

	_completeFlush(batch, error) {
		this.inFlight = false;

		if (error) {
			if (this.retryCount < this.maxRetries) {
				this.retryCount += 1;
				this.queue = batch.concat(this.queue);
				this._scheduleFlush(this.flushInterval * 2);
				return;
			}
			this.retryCount = 0;
		} else {
			this.retryCount = 0;
		}

		if (this.queue.length >= this.batchSize) {
			this._flush();
		} else {
			this._scheduleFlush(this.flushInterval);
		}
	}

	_flush() {
		if (!this.queue.length || this.inFlight) return;
		var batch = this.queue.splice(0, this.batchSize);
		clearTimeout(this.timer);
		this.timer = null;
		var abortController = (typeof AbortController !== 'undefined') ? new AbortController() : null;
		var timeoutId = null;

		try {
			var body = JSON.stringify({
				serviceName: this.serviceName,
				environment: this.environment,
				sentAt: nowIso(),
				build: this.build,
				mode: this.mode,
				release: this.release,
				clientSessionId: this.clientSessionId,
				pageViewId: this.pageViewId,
				events: batch
			});

			this.inFlight = true;
			if (abortController && this.requestTimeout > 0) {
				timeoutId = setTimeout(function () { abortController.abort(); }, this.requestTimeout);
			}

			var requestOptions = {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'same-origin',
				keepalive: true,
				body: body
			};
			if (abortController) {
				requestOptions.signal = abortController.signal;
			}

			fetch(this.endpoint, requestOptions).then((res) => {
				if (timeoutId) clearTimeout(timeoutId);
				if (this.debug && typeof console !== 'undefined') console.log('[rum] flush', res && res.status, batch.length);
				if (res && res.ok === false) {
					throw new Error('RUM endpoint returned HTTP ' + res.status);
				}
				this._completeFlush(batch, null);
			}).catch((e) => {
				if (timeoutId) clearTimeout(timeoutId);
				if (this.debug && typeof console !== 'undefined') console.warn('[rum] flush failed', e);
				this._completeFlush(batch, e);
			});
		} catch (e) {
			if (timeoutId) clearTimeout(timeoutId);
			if (this.debug && typeof console !== 'undefined') console.warn('[rum] flush failed (sync)', e);
			this._completeFlush(batch, e);
		}
	}

	// Сумісність із кодом, що очікує транзакції
	startTransaction(name, type) {
		var started = Date.now();
		var ended = false;
		return {
			end: () => {
				if (ended) return;
				ended = true;
				var durationMs = Date.now() - started;
				this.captureMessage('transaction', {
					name: name,
					type: type || 'custom',
					durationMs: durationMs,
					source: 'rum-reporter'
				});
			}
		};
	}
}

export {
	shouldReloadOnChunkError,
	extractReloadableStaticAssetUrl,
	createChunkLoadErrorHandler,
	subscribeErrorReporterWindowError,
	subscribeErrorReporterUnhandledRejection,
};

var reporter = new ErrorReporter();
export default reporter;
